import { z } from 'zod';

import { NavigationConfigSchema } from '@kit/ui/navigation-schema';

import { sectionsToConfigRoutes } from '~/config/navigation/to-nav-config';
import type { Brand } from '~/lib/brand';
import type {
  NavContext,
  NavSection,
  PartnerCompetitionRef,
} from './navigation/nav-types';
import { resolveSections } from './navigation/resolve-roles';

interface GetRoutesOptions {
  isSuperAdmin?: boolean;
  isClientUser?: boolean;
  isDancer?: boolean;
  isOrganizer?: boolean;
  isSeller?: boolean;
  hasPerformanceData?: boolean;
  needsProfileSetup?: boolean;
  hasPendingOrgJoinRequest?: boolean;
  clientRole?: 'owner' | 'member' | null;
  brand?: Brand;
  partnerCompetitions?: PartnerCompetitionRef[];
  dancerProfileSlug?: string | null;
}

/**
 * Navigation configuration based on user personas. The (role x brand) decision
 * now lives in ONE place — the declarative matrix in config/navigation/ —
 * which this façade resolves and adapts to the navigation-schema route shape.
 *
 * 1. DANCERS: brand-aware daily/discovery + profile + account.
 * 2. CLIENT USERS: Flow-only Client Portal groups.
 * 3. SUPER ADMINS: Favorites + a collapsed Admin-tools section + the brand's
 *    user surfaces, symmetric across Flow and Ballee.
 *
 * Full design + per-cell ordering: config/navigation/nav-matrix.ts.
 */
function toNavContext(options: GetRoutesOptions): NavContext {
  return {
    isSuperAdmin: options.isSuperAdmin ?? false,
    isClientUser: options.isClientUser ?? false,
    isDancer: options.isDancer ?? false,
    isOrganizer: options.isOrganizer ?? false,
    isSeller: options.isSeller ?? false,
    hasPerformanceData: options.hasPerformanceData ?? false,
    needsProfileSetup: options.needsProfileSetup ?? false,
    hasPendingOrgJoinRequest: options.hasPendingOrgJoinRequest ?? false,
    clientRole: options.clientRole ?? null,
    brand: options.brand ?? 'flow',
    partnerCompetitions: options.partnerCompetitions ?? [],
    dancerProfileSlug: options.dancerProfileSlug ?? null,
  };
}

/**
 * The resolved declarative sections, including the `favorites` / `adminTools`
 * sentinels the section-dispatch render surfaces (sidebar, header, mobile)
 * walk directly. Use this in the render layer; use
 * getPersonalAccountNavigationConfig for the schema-validated badge renderer.
 */
export function getResolvedNavSections(
  options: GetRoutesOptions = {},
): NavSection[] {
  return resolveSections(toNavContext(options));
}

export function getPersonalAccountNavigationConfig(
  options: GetRoutesOptions = {},
) {
  const routes = sectionsToConfigRoutes(
    getResolvedNavSections(options),
  ) satisfies z.infer<typeof NavigationConfigSchema>['routes'];

  return NavigationConfigSchema.parse({
    routes,
    style: process.env.NEXT_PUBLIC_USER_NAVIGATION_STYLE,
    // BAL-555: default the /home sidebar to EXPANDED so section labels show.
    // The shared makerkit schema defaults sidebarCollapsed to 'true' +
    // collapsedStyle 'icon', which renders the sidebar icon-only (no section
    // text) for any user without a saved `sidebar:state:v2` cookie — Philippine
    // reported this as "just emojis". Fall back to 'false' when the env var is
    // unset so Ballee opens expanded; an explicit env value still wins.
    sidebarCollapsed: process.env.NEXT_PUBLIC_HOME_SIDEBAR_COLLAPSED ?? 'false',
    sidebarCollapsedStyle: process.env.NEXT_PUBLIC_SIDEBAR_COLLAPSIBLE_STYLE,
  });
}

// Keep default export for backward compatibility
export const personalAccountNavigationConfig =
  getPersonalAccountNavigationConfig();
